import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { CurrentCarePlan, TelemonitoringInfo } from "@/pages/patient-settings/CurrentCarePlan.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { PatientRenewalAction } from "@/uiKit/molecules/CarePlan/PatientRenewalAction.tsx";
import { CarePlansHistoryCard } from "@/uiKit/organisms/CarePlansHistoryCard.tsx";
import { PatientRenewal } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx";

export type TelemonitoringCardProps = {
  patientId: string;
  className?: string;
};

export const TelemonitoringCard: React.FC<TelemonitoringCardProps> = ({ patientId, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const [isCarePlanHistoryOpen, setIsCarePlanHistoryOpen] = React.useState(false);

  const currentCarePlanInfo = Queries.practitioner.useCurrentCarePlanInfo(patientId);
  const me = Queries.user.usePractitionerMe({});

  return (
    <Card className={cx(styles.container, className)}>
      <CardHeader id="tls-title" title={t("pages.patientSettings.telemonitoring.title")} />
      <CardContent className={styles.content}>
        {currentCarePlanInfo.currentCarePlan ? (
          <CurrentCarePlan carePlan={currentCarePlanInfo.currentCarePlan} patientId={patientId} />
        ) : (
          <TelemonitoringInfo patientId={patientId} />
        )}
      </CardContent>
      <CardFooter className={styles.footer}>
        <Button
          data-action="care-plan.history.navigate"
          variant="outlined"
          startIcon={<Icons.timeRewind className={styles.icon} />}
          onClick={() => setIsCarePlanHistoryOpen(true)}
        >
          {t("carePlans.history")}
        </Button>
        {me.qualification === "doctor" && <TelemonitoringRenewal patientId={patientId} />}
      </CardFooter>
      <Dialog open={isCarePlanHistoryOpen} onClose={() => setIsCarePlanHistoryOpen(false)} maxWidth={1250}>
        <CarePlansHistoryCard onClose={() => setIsCarePlanHistoryOpen(false)} />
      </Dialog>
    </Card>
  );
};

type TelemonitoringRenewalProps = {
  patientId: string;
};

const TelemonitoringRenewal: React.FC<TelemonitoringRenewalProps> = ({ patientId }) => {
  const renewalInfo = Queries.practitioner.usePatientRenewInfo(patientId);
  const [isRenewalOpen, setIsRenewalOpen] = React.useState(false);
  return (
    <>
      <PatientRenewalAction renewalInfo={renewalInfo} dataSource="settings" onClick={() => setIsRenewalOpen(true)} />
      {renewalInfo.renewalType && (
        <PatientRenewal
          renewalType={renewalInfo.renewalType}
          patientId={patientId}
          open={isRenewalOpen}
          onClose={() => setIsRenewalOpen(false)}
          onSubmit={() => setIsRenewalOpen(false)}
        />
      )}
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
  footer: css`
    justify-content: flex-end;
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
